import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import config from "../../../../config";
import {
  searchHaklaiLotParcelFromAddress
} from "../../services/haklaiGovMapService";
import {
  generateHaklai2990Json,
  generateHaklai2990Pdf,
  generateHaklaiDeductions,
  getHaklai2990Fields,
  updateHaklai2990Fields
} from "../../services/haklaiDocumentsService";
import {
  DOC_2990_AUTOFILL_TARGET_FIELDS,
  DOC_2990_NUMERIC_FIELDS,
  EMPTY_DOC_2990_FIELDS,
  buildPropertyAutofillPatch,
  getHaklai2990ExportGroups,
  getParcelPairCacheKey,
  getSeededDocFields,
  getSmartAddressLookupKey,
  normalizeDocFieldsForPersistence,
  normalizeDocFieldValueForState,
  normalizeFieldValue,
  parseSmartLookupAddress,
  validate2990JsonFieldsDetailed
} from "./haklai2990Helpers";
import {
  HAKLAI_2990_MESSAGES
} from "./haklai2990Texts";
import { confirmTaxAuthorityDocumentGenerationOncePerDeal } from "../../../shared/dialogs/taxAuthorityDocumentConfirm";

const ADDRESS_LOOKUP_DEBOUNCE_MS = 250;
const ADDRESS_LOOKUP_DUPLICATE_WINDOW_MS = 700;

const useHaklai2990Panel = ({
  analyzedPreCalc,
  dealId,
  haklaiDraftPreCalc,
  taxAuthorityAccountIdentity = {}
}) => {
  const {
    fullName,
    licenseNumber,
    disableTaxAuthorityDocumentConfirmation
  } = taxAuthorityAccountIdentity;
  const parcelLookupReq = useRef(0);
  const addressLookupDebounceRef = useRef(null);
  const parcelAddressSearchRef = useRef("");
  const documentActionInFlightRef = useRef(false);
  const lastLookupAttemptRef = useRef({ key: "", at: 0 });
  const lastAddressLookupCacheRef = useRef({
    key: "",
    parsedAddress: null,
    pairs: [],
    patchByPair: {}
  });
  const parcelMenuAnchorRef = useRef(null);
  const mountedRef = useRef(true);
  const draftParcelSeed = useMemo(() => ({
    parcelAddressSearch: normalizeFieldValue(haklaiDraftPreCalc?.parcelAddressSearch),
    parcelGush: normalizeFieldValue(haklaiDraftPreCalc?.parcelGush),
    parcelHelka: normalizeFieldValue(haklaiDraftPreCalc?.parcelHelka)
  }), [
    haklaiDraftPreCalc?.parcelAddressSearch,
    haklaiDraftPreCalc?.parcelGush,
    haklaiDraftPreCalc?.parcelHelka
  ]);
  const [docFields, setDocFields] = useState(EMPTY_DOC_2990_FIELDS);
  const docFieldsRef = useRef(EMPTY_DOC_2990_FIELDS);
  const [parcelAddressSearch, setParcelAddressSearch] = useState(
    draftParcelSeed.parcelAddressSearch
  );
  const [parcelLookupError, setParcelLookupError] = useState(null);
  const [addressMenuAnchor, setAddressMenuAnchor] = useState(null);
  const [addressChoices, setAddressChoices] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const [hasGovMapAutofill, setHasGovMapAutofill] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingDeductions, setIsGeneratingDeductions] = useState(false);
  const [activeJsonButton, setActiveJsonButton] = useState("");
  const [jsonFieldErrors, setJsonFieldErrors] = useState({});
  const [jsonValidationFocusRequest, setJsonValidationFocusRequest] = useState(null);

  useEffect(() => {
    docFieldsRef.current = docFields;
  }, [docFields]);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    setIsLoading(true);
    setLoadError("");
    parcelAddressSearchRef.current = draftParcelSeed.parcelAddressSearch;
    setParcelAddressSearch(draftParcelSeed.parcelAddressSearch);
    setParcelLookupError(null);
    setHasGovMapAutofill(false);
    if (addressLookupDebounceRef.current) {
      window.clearTimeout(addressLookupDebounceRef.current);
      addressLookupDebounceRef.current = null;
    }
    lastLookupAttemptRef.current = { key: "", at: 0 };
    lastAddressLookupCacheRef.current = {
      key: "",
      parsedAddress: null,
      pairs: [],
      patchByPair: {}
    };

    getHaklai2990Fields(dealId).then((res) => {
      if (cancelled) return;

      if (!res?.success) {
        const seededFields = getSeededDocFields({
          serverFields: {},
          haklaiDraftPreCalc: draftParcelSeed
        });
        docFieldsRef.current = seededFields;
        setDocFields(seededFields);
        setLoadError(HAKLAI_2990_MESSAGES.loadError);
        setIsLoading(false);
        return;
      }

      const seededFields = getSeededDocFields({
        serverFields: res.data || {},
        haklaiDraftPreCalc: draftParcelSeed
      });
      docFieldsRef.current = seededFields;
      setDocFields(seededFields);
      setIsLoading(false);
    }).catch(() => {
      if (cancelled) return;
      const seededFields = getSeededDocFields({
        serverFields: {},
        haklaiDraftPreCalc: draftParcelSeed
      });
      docFieldsRef.current = seededFields;
      setDocFields(seededFields);
      setLoadError(HAKLAI_2990_MESSAGES.loadError);
      setIsLoading(false);
    });

    return () => {
      if (addressLookupDebounceRef.current) {
        window.clearTimeout(addressLookupDebounceRef.current);
        addressLookupDebounceRef.current = null;
      }
      parcelLookupReq.current += 1;
      mountedRef.current = false;
      cancelled = true;
    };
  }, [
    dealId,
    draftParcelSeed
  ]);

  const closeAddressParcelMenu = useCallback(() => {
    setAddressMenuAnchor(null);
    setAddressChoices([]);
  }, []);

  const clearPendingAddressLookup = useCallback(() => {
    if (!addressLookupDebounceRef.current) return;
    window.clearTimeout(addressLookupDebounceRef.current);
    addressLookupDebounceRef.current = null;
  }, []);

  const getCurrentLookupKey = useCallback(() => (
    getSmartAddressLookupKey(parseSmartLookupAddress(parcelAddressSearchRef.current))
  ), []);

  const invalidateAddressLookup = useCallback(() => {
    parcelLookupReq.current += 1;
    clearPendingAddressLookup();
    lastLookupAttemptRef.current = { key: "", at: 0 };
    setAddressSearchLoading(false);
  }, [clearPendingAddressLookup]);

  const applyDocFieldsPatch = useCallback((patch = {}) => {
    setDocFields((prev) => {
      const next = {
        ...prev,
        ...patch
      };
      docFieldsRef.current = next;
      return next;
    });
  }, []);

  const getResolvedAutofillPatch = useCallback((lot, parcel, parsedAddress) => {
    const fallbackAddress = parsedAddress || parseSmartLookupAddress(parcelAddressSearch);
    return buildPropertyAutofillPatch({
      lot,
      parcel,
      resolvedAddress: fallbackAddress,
      parsedAddress: fallbackAddress
    });
  }, [parcelAddressSearch]);

  const commitAutofillResult = useCallback(async (
    lot,
    parcel,
    parsedAddress,
    lookupKey,
    isLookupCurrent = () => true
  ) => {
    const pairKey = getParcelPairCacheKey(lot, parcel);
    const activeLookupKey = lookupKey || getSmartAddressLookupKey(parsedAddress);
    const cache = lastAddressLookupCacheRef.current;

    let autofillPatch =
      cache.key === activeLookupKey && cache.patchByPair && cache.patchByPair[pairKey]
        ? cache.patchByPair[pairKey]
        : null;

    if (!autofillPatch) {
      autofillPatch = await getResolvedAutofillPatch(lot, parcel, parsedAddress);
      if (cache.key === activeLookupKey) {
        cache.patchByPair = {
          ...(cache.patchByPair || {}),
          [pairKey]: autofillPatch
        };
      }
    }

    if (!isLookupCurrent()) return false;
    applyDocFieldsPatch(autofillPatch);
    parcelAddressSearchRef.current = "";
    setParcelAddressSearch("");
    setParcelLookupError(null);
    setHasGovMapAutofill(true);
    return true;
  }, [applyDocFieldsPatch, getResolvedAutofillPatch]);

  const runAddressLookup = useCallback(async () => {
    if (documentActionInFlightRef.current) return;

    const trimmedAddress = String(parcelAddressSearch || "").trim();
    const parsedAddress = parseSmartLookupAddress(trimmedAddress);
    if (!parsedAddress) {
      invalidateAddressLookup();
      closeAddressParcelMenu();
      setAddressChoices([]);
      setHasGovMapAutofill(false);
      setParcelLookupError({
        scope: "address",
        message: HAKLAI_2990_MESSAGES.smartAddressFormatError
      });
      return;
    }

    const lookupKey = getSmartAddressLookupKey(parsedAddress);
    const now = Date.now();
    if (
      lastLookupAttemptRef.current.key === lookupKey
      && now - lastLookupAttemptRef.current.at < ADDRESS_LOOKUP_DUPLICATE_WINDOW_MS
    ) {
      return;
    }
    lastLookupAttemptRef.current = { key: lookupKey, at: now };
    clearPendingAddressLookup();

    const token = config.govMapApiToken;
    if (!token || !String(token).trim()) {
      parcelLookupReq.current += 1;
      setAddressSearchLoading(false);
      setParcelLookupError({
        scope: "address",
        message: HAKLAI_2990_MESSAGES.govMapUnavailable
      });
      return;
    }

    const req = ++parcelLookupReq.current;
    closeAddressParcelMenu();
    setParcelLookupError(null);
    setAddressSearchLoading(true);
    setHasGovMapAutofill(false);
    const isActiveLookupRequest = () => (
      mountedRef.current
      && req === parcelLookupReq.current
    );
    const isCurrentLookup = () => (
      isActiveLookupRequest()
      && getCurrentLookupKey() === lookupKey
    );

    const timeoutId = window.setTimeout(async () => {
      if (addressLookupDebounceRef.current === timeoutId) {
        addressLookupDebounceRef.current = null;
      }
      try {
        const cache = lastAddressLookupCacheRef.current;
        let pairs;

        if (cache.key === lookupKey && Array.isArray(cache.pairs)) {
          pairs = cache.pairs;
        } else {
          pairs = await searchHaklaiLotParcelFromAddress(token, parsedAddress.lookupAddress);
          lastAddressLookupCacheRef.current = {
            key: lookupKey,
            parsedAddress,
            pairs,
            patchByPair: {}
          };
        }

        if (!isCurrentLookup()) return;

        if (pairs.length === 0) {
          setParcelLookupError({
            scope: "address",
            message: HAKLAI_2990_MESSAGES.addressNoMatch
          });
          return;
        }

        if (pairs.length === 1) {
          setParcelLookupError(null);
          await commitAutofillResult(
            pairs[0].gush,
            pairs[0].helka,
            parsedAddress,
            lookupKey,
            isCurrentLookup
          );
          return;
        }

        setAddressChoices(pairs);
        setAddressMenuAnchor(parcelMenuAnchorRef.current);
        setParcelLookupError(null);
      } catch {
        if (!isCurrentLookup()) return;
        setParcelLookupError({
          scope: "address",
          message: HAKLAI_2990_MESSAGES.addressSearchFailed
        });
      } finally {
        if (isActiveLookupRequest()) {
          setAddressSearchLoading(false);
        }
      }
    }, ADDRESS_LOOKUP_DEBOUNCE_MS);
    addressLookupDebounceRef.current = timeoutId;
  }, [
    clearPendingAddressLookup,
    commitAutofillResult,
    closeAddressParcelMenu,
    getCurrentLookupKey,
    invalidateAddressLookup,
    parcelAddressSearch
  ]);

  const handleAddressSearchClick = useCallback(() => {
    runAddressLookup();
  }, [runAddressLookup]);

  const handleAddressKeyPress = useCallback((e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    runAddressLookup();
  }, [runAddressLookup]);

  const handleDocFieldChange = useCallback((e) => {
    if (documentActionInFlightRef.current) return;
    const fieldName = e.target.name;
    const nextValue = DOC_2990_NUMERIC_FIELDS.has(fieldName)
      ? e.target.value
      : normalizeDocFieldValueForState(fieldName, e.target.value);

    if (
      parcelLookupError?.scope === "parcel"
      && (fieldName === "blockNum" || fieldName === "subBlock")
    ) {
      setParcelLookupError(null);
    }

    if (DOC_2990_AUTOFILL_TARGET_FIELDS.includes(fieldName)) {
      invalidateAddressLookup();
      closeAddressParcelMenu();
      setHasGovMapAutofill(false);
    }

    if (jsonFieldErrors[fieldName]) {
      setJsonFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }

    setDocFields((prev) => {
      const next = {
        ...prev,
        [fieldName]: nextValue
      };
      docFieldsRef.current = next;
      return next;
    });
  }, [closeAddressParcelMenu, invalidateAddressLookup, jsonFieldErrors, parcelLookupError]);

  const handleParcelSearchFieldChange = useCallback((e) => {
    if (documentActionInFlightRef.current) return;
    const nextValue = e.target.value;
    parcelAddressSearchRef.current = nextValue;
    invalidateAddressLookup();
    setParcelAddressSearch(nextValue);
    if (parcelLookupError?.scope === "address") setParcelLookupError(null);
    if (addressChoices.length) closeAddressParcelMenu();
    if (hasGovMapAutofill) setHasGovMapAutofill(false);
  }, [
    addressChoices.length,
    closeAddressParcelMenu,
    hasGovMapAutofill,
    invalidateAddressLookup,
    parcelLookupError
  ]);

  const handleAddressChoiceClick = useCallback(async (pair) => {
    if (documentActionInFlightRef.current) return;
    closeAddressParcelMenu();
    setParcelLookupError(null);
    setAddressSearchLoading(true);
    const parsedAddress = parseSmartLookupAddress(parcelAddressSearch);
    const lookupKey = getSmartAddressLookupKey(parsedAddress);
    const req = ++parcelLookupReq.current;
    const isCurrentLookup = () => (
      mountedRef.current
      && req === parcelLookupReq.current
      && getCurrentLookupKey() === lookupKey
    );
    try {
      await commitAutofillResult(
        pair.gush,
        pair.helka,
        parsedAddress,
        lookupKey,
        isCurrentLookup
      );
    } finally {
      if (isCurrentLookup()) {
        setAddressSearchLoading(false);
      }
    }
  }, [closeAddressParcelMenu, commitAutofillResult, getCurrentLookupKey, parcelAddressSearch]);

  const exportGroups = useMemo(
    () => getHaklai2990ExportGroups(analyzedPreCalc),
    [analyzedPreCalc]
  );

  const persistDocFields = useCallback(async (
    showSuccess,
    fieldsSnapshot = docFields,
    options = {}
  ) => {
    const showSavingIndicator = options.showSavingIndicator !== false;
    if (showSavingIndicator) setIsSaving(true);
    try {
      const res = await updateHaklai2990Fields(dealId, fieldsSnapshot);
      if (!res?.success) {
        Swal.fire(
          HAKLAI_2990_MESSAGES.saveErrorTitle,
          res?.message || HAKLAI_2990_MESSAGES.saveFailed,
          "error"
        );
        return false;
      }
      if (showSuccess) {
        Swal.fire(HAKLAI_2990_MESSAGES.saveSucceeded, "", "success");
      }
      return true;
    } catch {
      Swal.fire(
        HAKLAI_2990_MESSAGES.saveErrorTitle,
        HAKLAI_2990_MESSAGES.saveFailed,
        "error"
      );
      return false;
    } finally {
      if (showSavingIndicator) setIsSaving(false);
    }
  }, [dealId, docFields]);

  const beginDocumentAction = useCallback(() => {
    if (documentActionInFlightRef.current) return false;
    documentActionInFlightRef.current = true;
    invalidateAddressLookup();
    closeAddressParcelMenu();
    return true;
  }, [closeAddressParcelMenu, invalidateAddressLookup]);

  const finishDocumentAction = useCallback(() => {
    documentActionInFlightRef.current = false;
  }, []);

  const confirmTaxAuthorityDocumentGenerationOnce = useCallback(async () => {
    return confirmTaxAuthorityDocumentGenerationOncePerDeal({
      dealId,
      fullName,
      licenseNumber,
      disableTaxAuthorityDocumentConfirmation
    });
  }, [dealId, fullName, licenseNumber, disableTaxAuthorityDocumentConfirmation]);

  const handleSave = useCallback(async () => {
    if (!beginDocumentAction()) return;
    const fieldsSnapshot = normalizeDocFieldsForPersistence(docFields);
    try {
      await persistDocFields(true, fieldsSnapshot);
    } finally {
      finishDocumentAction();
    }
  }, [beginDocumentAction, docFields, finishDocumentAction, persistDocFields]);

  const handleGeneratePdf = useCallback(async () => {
    if (!beginDocumentAction()) return;
    const fieldsSnapshot = normalizeDocFieldsForPersistence(docFields);
    try {
      const confirmed = await confirmTaxAuthorityDocumentGenerationOnce();
      if (!confirmed) return;
      setIsGeneratingPdf(true);
      const didPersist = await persistDocFields(
        false,
        fieldsSnapshot,
        { showSavingIndicator: false }
      );
      if (!didPersist) return;
      await generateHaklai2990Pdf({ ...fieldsSnapshot, dealId });
    } catch {
      Swal.fire(
        HAKLAI_2990_MESSAGES.documentGenerationFailedTitle,
        HAKLAI_2990_MESSAGES.documentGenerationFailedText,
        "warning"
      );
    } finally {
      setIsGeneratingPdf(false);
      finishDocumentAction();
    }
  }, [
    beginDocumentAction,
    confirmTaxAuthorityDocumentGenerationOnce,
    dealId,
    docFields,
    finishDocumentAction,
    persistDocFields
  ]);

  const handleGenerateDeductions = useCallback(async () => {
    if (!beginDocumentAction()) return;
    try {
      const confirmed = await confirmTaxAuthorityDocumentGenerationOnce();
      if (!confirmed) return;
      setIsGeneratingDeductions(true);
      await generateHaklaiDeductions(dealId);
    } catch {
      Swal.fire(
        HAKLAI_2990_MESSAGES.documentGenerationFailedTitle,
        HAKLAI_2990_MESSAGES.documentGenerationFailedText,
        "warning"
      );
    } finally {
      setIsGeneratingDeductions(false);
      finishDocumentAction();
    }
  }, [beginDocumentAction, confirmTaxAuthorityDocumentGenerationOnce, dealId, finishDocumentAction]);

  const handleGenerateJson = useCallback(async (dealTaxType, dealIndex) => {
    if (!beginDocumentAction()) return;
    const fieldsSnapshot = normalizeDocFieldsForPersistence(docFields);

    const buttonKey = `${dealTaxType}-${dealIndex}`;
    setActiveJsonButton(buttonKey);
    try {
      const validationError = validate2990JsonFieldsDetailed(fieldsSnapshot);
      if (validationError.message) {
        setJsonFieldErrors(validationError.fieldErrors);
        setJsonValidationFocusRequest({
          fieldName: validationError.firstFieldName,
          requestId: Date.now()
        });
        return;
      }
      setJsonFieldErrors({});
      const didPersist = await persistDocFields(
        false,
        fieldsSnapshot,
        { showSavingIndicator: false }
      );
      if (!didPersist) return;
      await generateHaklai2990Json({
        docFields: fieldsSnapshot,
        dealId,
        dealTaxType,
        dealIndex,
        partsAmount: exportGroups.length || 1
      });
    } catch (err) {
      if (err?.fieldErrors) {
        setJsonFieldErrors(err.fieldErrors);
        setJsonValidationFocusRequest({
          fieldName: err.firstFieldName || Object.keys(err.fieldErrors)[0] || "",
          requestId: Date.now()
        });
        return;
      }

      Swal.fire(
        HAKLAI_2990_MESSAGES.documentGenerationFailedTitle,
        HAKLAI_2990_MESSAGES.documentGenerationFailedText,
        "warning"
      );
    } finally {
      setActiveJsonButton("");
      finishDocumentAction();
    }
  }, [
    beginDocumentAction,
    dealId,
    docFields,
    exportGroups.length,
    finishDocumentAction,
    persistDocFields
  ]);

  const isDocumentActionBusy = isSaving || isGeneratingPdf || isGeneratingDeductions || activeJsonButton.length > 0;

  return {
    activeJsonButton,
    addressChoices,
    addressMenuAnchor,
    addressSearchLoading,
    closeAddressParcelMenu,
    docFields,
    exportGroups,
    handleAddressChoiceClick,
    handleAddressKeyPress,
    handleAddressSearchClick,
    handleDocFieldChange,
    handleGenerateDeductions,
    handleGenerateJson,
    handleGeneratePdf,
    handleParcelSearchFieldChange,
    handleSave,
    hasGovMapAutofill,
    isDocumentActionBusy,
    isGeneratingDeductions,
    isGeneratingPdf,
    isLoading,
    isSaving,
    jsonFieldErrors,
    jsonValidationFocusRequest,
    loadError,
    parcelAddressError: parcelLookupError?.scope === "address" ? parcelLookupError.message : "",
    parcelAddressSearch,
    parcelMenuAnchorRef,
    parcelPairError: parcelLookupError?.scope === "parcel" ? parcelLookupError.message : ""
  };
};

export default useHaklai2990Panel;
